import { useEffect, useState, useCallback } from "react";
import img1 from "../../assets/images/ach-img1.png";
import img2 from "../../assets/images/ach-img2.svg";
import img3 from "../../assets/images/ach-img3.svg";

// Custom hook for managing loader animations
const useLoaderAnimation = (selectedIndex: number, duration: number = 7500) => {
  const [animationStates, setAnimationStates] = useState([false, false, false]);
  const [progressStates, setProgressStates] = useState([0, 0, 0]);

  const startAnimation = useCallback(
    (index: number) => {
      // Reset all previous animations
      setAnimationStates((prev) => prev.map(() => false));
      setProgressStates((prev) => prev.map(() => 0));

      // Start new animation
      setAnimationStates((prev) => prev.map((_, i) => i === index));
      setProgressStates((prev) => prev.map((_, i) => (i === index ? 0 : 0)));

      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        setProgressStates((prev) =>
          prev.map((_, i) => (i === index ? progress : 0))
        );

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
        // Remove the reset logic - let the next animation handle the state change
      };

      requestAnimationFrame(animate);
    },
    [duration]
  );

  useEffect(() => {
    startAnimation(selectedIndex);
  }, [selectedIndex, startAnimation]);

  return { animationStates, progressStates };
};

export default function Achievements() {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const infograpgh = [
    {
      title: "Fortune 1 Contact Center Project (2017)",
      description:
        "Saved $338,000 annually using Lean Six Sigma methodologies and Oracle-based customer service templates.",
      img: img1,
      alt: "Bar chart showing annual savings growth through 2017",
    },

    {
      title: "L'Oréal USA Manufacturing Project (18 Months)",
      description:
        "Successfully launched two new products (Infallible Tinted Loose Setting Powder), consistently meeting or exceeding the 10,000-unit daily quota and increasing Overall Equipment Effectiveness (OEE) by 20% using PDCA cycles and Pareto analysis.",
      img: img2,
      alt: "Indexed OEE chart showing a 20 percent improvement from baseline",
    },
    {
      title: "Remington Arms Experience (2006-2011)",
      description:
        "Developed foundational expertise in quality management and Six Sigma across various manufacturing roles, emphasizing teamwork, quality control, and operational efficiency.",
      img: img3,
      alt: "DMAIC quality framework diagram for Lean Six Sigma methodology",
    },
  ];

  // Use the custom hook for loader animations
  const { animationStates, progressStates } = useLoaderAnimation(
    selectedIndex,
    7500
  );

  // timer to change the image every 7.5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setSelectedIndex((prevIndex) => (prevIndex + 1) % infograpgh.length);
    }, 7500);
    return () => clearInterval(interval);
  }, [selectedIndex]); // Reset timer when selectedIndex changes

  // Helper function to get loader styles
  const getLoaderStyles = (index: number) => {
    const isActive = animationStates[index];
    const progress = progressStates[index];

    if (!isActive) {
      return {
        width: "13px",
        height: "13px",
        background: "#E6F1FF",
        transition: "background 0.3s ease",
      };
    }

    // Width is immediately 200px when active
    const width = "200px";

    // Create gradient background that transitions from left to right
    // Start with #E6F1FF and transition to #2B8CFF based on progress
    const background = `linear-gradient(to right, #2B8CFF 0%, #2B8CFF ${
      progress * 100
    }%, #E6F1FF ${progress * 100}%, #E6F1FF 100%)`;

    return {
      width: width,
      height: "13px",
      background: background,
      transition: "background 0.1s linear",
    };
  };

  return (
    <div className="container relative py-16 lg:py-36 flex lg:flex-row flex-col">
      <div className="flex-col justify-start items-start flex">
        <div className="flex-col justify-start items-center gap-8 inline-flex">
          <div className="flex-col justify-start items-center gap-2 flex">
            <div className="p-2 bg-[#e6f1ff] rounded-md justify-center items-center gap-2 inline-flex">
              <div className="whitespace-nowrap text-[#2b8cff] text-base font-medium font-secondary leading-[19px]">
                KEY ACHIEVEMENTS
              </div>
            </div>
            <div className="text-center max-w-[660px] w-full text-[#0e0e0e] text-2xl lg:text-[32px] font-semibold font-primary leading-[41.60px]">
              Proven Results and Notable Successes
            </div>
          </div>
          <div className="flex flex-col lg:flex-row justify-center items-center gap-10 lg:gap-20">
            <div className="flex-col justify-start items-start gap-10 flex">
              <div className="justify-start items-center gap-2 flex">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="rounded-full"
                    style={getLoaderStyles(index)}
                  />
                ))}
              </div>
              <div className="flex-col justify-start items-start gap-10 flex">
                {infograpgh.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedIndex(index)}
                    className={`cursor-pointer flex-col justify-start items-start gap-2 flex ${
                      selectedIndex === index
                        ? "opacity-100 h-[188px] lg:h-auto"
                        : "opacity-10 hidden lg:flex"
                    }`}
                  >
                    <div className="text-[#0e0e0e] text-xl font-normal font-primary leading-7">
                      {item.title}
                    </div>
                    <div className="text-[#373737] text-base font-light font-primary leading-[22.40px] tracking-[0.01em]">
                      {item.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <img
              src={infograpgh[selectedIndex].img}
              alt={infograpgh[selectedIndex].alt}
              className="w-[470px] h-full"
            />
          </div>
        </div>
        <img
          src="https://storage.googleapis.com/uxpilot-auth.appspot.com/113049385656753869919/Rectangle1-a68316c2-78ee-4f6e-bf18-74533358889d.svg"
          alt="Rectangle 1"
          className="left-[22px] top-0 absolute w-10 lg:w-auto"
        />
        <img
          src="https://storage.googleapis.com/uxpilot-auth.appspot.com/113049385656753869919/image13-f1155cdc-e521-444e-9e22-4a74ab2387ce.svg"
          alt="image 13"
          className="right-[22px] top-[48px] absolute rotate-[15deg]"
        />
      </div>
    </div>
  );
}
